import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { money, catIcon } from "../lib/api";
import { Button } from "../components/ui/button";
import { Plus, Wallet, Gift, TrendingDown, ArrowRight, CalendarClock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AddBillDialog from "../components/AddBillDialog";
import PayDialog from "../components/PayDialog";
import SwitchDialog from "../components/SwitchDialog";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [categories, setCategories] = useState([]);
  const [bills, setBills] = useState([]);
  const [offers, setOffers] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [preselected, setPreselected] = useState(null);
  const [payBill, setPayBill] = useState(null);
  const [switchOffer, setSwitchOffer] = useState(null);

  const load = useCallback(async () => {
    const [c, b, o, w] = await Promise.all([api.get("/categories"), api.get("/bills"), api.get("/offers"), api.get("/wallet")]);
    setCategories(c.data); setBills(b.data); setOffers(o.data); setWallet(w.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sym = wallet?.currency_symbol || "$";
  const unpaidBills = bills.filter((b) => b.status !== "paid");
  const activeCats = categories.filter((c) => !c.coming_soon).slice(0, 6);

  const openAdd = (cat = null) => { setPreselected(cat); setAddOpen(true); };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Hi, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-slate-500 mt-1">Manage every bill in one place — pay, save and earn rewards.</p>
        </div>
        <Button size="lg" onClick={() => openAdd(null)} data-testid="add-bill-cta" className="rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold px-7 py-6 shadow-lg shadow-emerald-500/20">
          <Plus className="w-5 h-5 mr-2" /> Add Bill
        </Button>
      </div>

      {/* Wallet + points */}
      <div className="grid lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 bg-slate-900 rounded-2xl p-7 text-white relative overflow-hidden" data-testid="wallet-card">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/10 rounded-full" />
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-slate-300"><Wallet className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-bold">Wallet</span></div>
              <p className="font-display text-4xl font-extrabold text-emerald-400 mt-2" data-testid="wallet-balance">{money(wallet?.balance, sym)}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-300"><Gift className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-bold">Reward points</span></div>
              <p className="font-display text-4xl font-extrabold text-amber-400 mt-2" data-testid="reward-points">{(wallet?.reward_points || 0).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Link to="/wallet"><Button className="rounded-full bg-white/10 hover:bg-white/20 text-white border-0 font-semibold" data-testid="view-wallet-btn">View wallet</Button></Link>
            <Link to="/referral"><Button className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white border-0 font-semibold">Refer & earn</Button></Link>
          </div>
        </motion.div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Categories</p>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {activeCats.map((c) => {
              const Icon = catIcon(c.icon);
              return (
                <button key={c.slug} onClick={() => openAdd(c)} data-testid={`cat-tile-${c.slug}`} className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Icon className="w-5 h-5 text-emerald-600" /></div>
                  <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight">{c.name}</span>
                </button>
              );
            })}
          </div>
          <Link to="/categories" className="text-sm font-semibold text-emerald-600 hover:underline flex items-center gap-1 mt-3" data-testid="view-all-categories">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>

      {/* MY BILLS */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-slate-900">My Bills</h2>
          <Link to="/bills" className="text-sm font-semibold text-emerald-600 hover:underline">View all</Link>
        </div>
        {bills.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
            <p className="font-display font-bold text-slate-900">No bills yet</p>
            <p className="text-slate-500 text-sm mt-1">Add your first bill to pay, save and earn rewards.</p>
            <Button onClick={() => openAdd(null)} className="mt-4 rounded-full bg-emerald-500 hover:bg-emerald-600 font-semibold"><Plus className="w-4 h-4 mr-2" /> Add Bill</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bills.slice(0, 6).map((b) => {
              const Icon = catIcon(categories.find((c) => c.slug === b.category_slug)?.icon);
              const isPaid = b.status === "paid";
              return (
                <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`bill-card-${b.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="w-4.5 h-4.5 text-slate-700" /></div>
                      <div>
                        <p className="font-display font-bold text-slate-900 leading-tight">{b.category_name}</p>
                        <p className="text-xs text-slate-400">{b.provider_name}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${isPaid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{isPaid ? "paid" : "unpaid"}</span>
                  </div>
                  <p className="font-display text-2xl font-extrabold text-slate-900 mt-3">{money(b.amount, sym)}</p>
                  {b.due_date && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><CalendarClock className="w-3.5 h-3.5" /> Due {b.due_date}</p>}
                  {!isPaid ? (
                    <Button onClick={() => setPayBill(b)} data-testid={`pay-btn-${b.id}`} className="w-full mt-4 rounded-full bg-emerald-500 hover:bg-emerald-600 font-semibold">PAY & SAVE</Button>
                  ) : (
                    <p className="text-sm font-semibold text-emerald-600 mt-4">✓ Paid</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* EXPLORE & SAVE */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-amber-500" />
          <h2 className="font-display text-xl font-bold text-slate-900">Explore & Save</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {offers.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={`offer-card-${o.id}`}>
              <span className="text-xs uppercase tracking-widest font-bold text-emerald-600">{o.category_slug}</span>
              <p className="font-display font-bold text-slate-900 mt-1">{o.title}</p>
              <p className="text-sm text-slate-500 mt-1 flex-1">{o.description}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-slate-400 line-through">{money(o.current_price, sym)}</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="font-display font-extrabold text-emerald-600 text-lg">{money(o.new_price, sym)}</span>
                <span className="text-xs text-slate-400">/{o.period}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">Save {money(o.saving, sym)}/{o.period}</span>
                {o.reward_points > 0 && <span className="text-xs font-bold text-emerald-700">+{o.reward_points} pts</span>}
              </div>
              <Button onClick={() => setSwitchOffer(o)} data-testid={`switch-btn-${o.id}`} className="w-full mt-4 rounded-full bg-slate-900 hover:bg-slate-800 font-semibold">SWITCH & SAVE</Button>
            </div>
          ))}
        </div>
      </section>

      <AddBillDialog open={addOpen} onOpenChange={setAddOpen} categories={categories} preselected={preselected} onSaved={() => { load(); refresh(); }} />
      <PayDialog open={!!payBill} onOpenChange={(o) => !o && setPayBill(null)} bill={payBill} onPaid={() => { load(); refresh(); }} />
      <SwitchDialog open={!!switchOffer} onOpenChange={(o) => !o && setSwitchOffer(null)} offer={switchOffer} onDone={() => { load(); refresh(); }} />
    </div>
  );
}
